import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface ArticleViewStats {
  article_id: string;
  total_views: number;
  unique_views: number;
  authenticated_views: number;
  last_viewed_at: string | null;
  first_viewed_at: string | null;
}

export interface DailyViewData {
  date: string;
  views: number;
  unique_views: number;
}

// Fetch aggregated view stats for all articles
export function useArticleViewStats() {
  return useQuery({
    queryKey: ["article-view-stats"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("help_article_view_stats")
        .select("*");

      if (error) throw error;
      return data as ArticleViewStats[];
    },
  });
}

// Fetch view stats for a specific article
export function useArticleViewStatsById(articleId: string) {
  return useQuery({
    queryKey: ["article-view-stats", articleId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("help_article_view_stats")
        .select("*")
        .eq("article_id", articleId)
        .maybeSingle();

      if (error) throw error;
      return data as ArticleViewStats | null;
    },
    enabled: !!articleId,
  });
}

// Fetch daily view data for charts (last N days)
export function useDailyViewData(days: number = 30, articleId?: string) {
  return useQuery({
    queryKey: ["article-daily-views", days, articleId],
    queryFn: async () => {
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);

      let query = supabase
        .from("help_article_views")
        .select("viewed_at, session_id")
        .gte("viewed_at", startDate.toISOString())
        .order("viewed_at", { ascending: true });

      if (articleId) {
        query = query.eq("article_id", articleId);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Group by date
      const dailyData: Record<string, { views: number; sessions: Set<string> }> = {};
      
      data?.forEach((view) => {
        const date = new Date(view.viewed_at).toISOString().split("T")[0];
        if (!dailyData[date]) {
          dailyData[date] = { views: 0, sessions: new Set() };
        }
        dailyData[date].views++;
        dailyData[date].sessions.add(view.session_id);
      });

      // Convert to array and fill missing dates
      const result: DailyViewData[] = [];
      const currentDate = new Date(startDate);
      const endDate = new Date();

      while (currentDate <= endDate) {
        const dateStr = currentDate.toISOString().split("T")[0];
        const dayData = dailyData[dateStr];
        result.push({
          date: dateStr,
          views: dayData?.views || 0,
          unique_views: dayData?.sessions.size || 0,
        });
        currentDate.setDate(currentDate.getDate() + 1);
      }

      return result;
    },
  });
}

// Fetch total views summary
export function useTotalViewsSummary() {
  return useQuery({
    queryKey: ["article-views-summary"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("help_article_views")
        .select("id, session_id, user_id");

      if (error) throw error;

      const sessions = new Set(data?.map((v) => v.session_id));
      const authenticatedUsers = new Set(
        data?.filter((v) => v.user_id).map((v) => v.user_id)
      );

      return {
        totalViews: data?.length || 0,
        uniqueSessions: sessions.size,
        authenticatedViews: authenticatedUsers.size,
      };
    },
  });
}
